/**
 * Raw Tables whitelist.
 *
 * Drives the /raw-tables page — a read-only browser for a curated set
 * of PostgREST endpoints used for production data verification when
 * the curated UI hides or transforms a field.
 *
 * Adding a table is a deliberate two-step act:
 *   1. add a `raw:<table>` page key (permissionShape: 'page') to
 *      config/permissions.ts so it can be granted to roles,
 *   2. add the matching entry below with an explicit `columns` list.
 *
 * Columns are listed explicitly — never `select=*` — so a future
 * migration that adds a sensitive column (PII, credentials, internal
 * routing IDs) does NOT silently leak it through this page.
 */
export interface RawTableColumn {
  key: string
  /** Optional label override; default is humanise(key). */
  label?: string
  /** Drives renderer + alignment. */
  type?: 'number' | 'date' | 'datetime' | 'boolean' | 'string' | 'json'
}

/**
 * PostgREST embedded resource — translates a FK column on the source
 * table into a human label by fetching `displayColumns` from the
 * target. Rendered as an extra column inserted right after `fk`.
 *
 * The select clause uses `<alias>:<table>(<displayColumns>)` so the
 * joined record is reachable as `row[alias]`. Requires that the FK
 * relationship be declared in the DB — PostgREST resolves embeds
 * from `pg_catalog`, not from this config.
 */
export interface RawTableJoin {
  /** FK column on this table that ties the rows together. */
  fk: string
  /** Alias used both in the PostgREST select and on the result row. */
  alias: string
  /** Target table name (as exposed via PostgREST). */
  table: string
  /** Columns to fetch from the target — joined with ' · ' for display. */
  displayColumns: string[]
  /** Column header for the joined display cell. */
  label: string
}

export interface RawTableDefinition {
  /** PostgREST endpoint without leading slash. */
  tableKey: string
  /** Permission key from config/permissions.ts (`raw:<table>`). */
  permissionKey: string
  /** Shown in the table-picker dropdown. */
  label: string
  /** Group label for the dropdown / docs. */
  group: string
  /** Ordered list of columns surfaced in the grid + detail modal. */
  columns: RawTableColumn[]
  /** FK joins inserted into the rendered grid after their `fk` column. */
  joins?: RawTableJoin[]
  /** Columns ilike-searched when the user types non-numeric text. */
  searchableColumns: string[]
  /**
   * When set, an all-digit search input filters by this column with
   * `=eq.<digits>`. Useful for "show all rows for event N" on tables
   * where text search across long-form columns isn't useful.
   */
  numericFilterColumn?: string
  defaultSort: { column: string; direction: 'asc' | 'desc' }
}

export const rawTables: RawTableDefinition[] = [
  {
    tableKey: 'events',
    permissionKey: 'raw:events',
    label: 'Events',
    group: 'Core',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'external_id' },
      { key: 'name' },
      { key: 'event_stream' },
      { key: 'status' },
      { key: 'property_id', type: 'number' },
      { key: 'start_date', type: 'date' },
      { key: 'end_date', type: 'date' },
      { key: 'min_participants', type: 'number' },
      { key: 'max_participants', type: 'number' },
      { key: 'overnight_guests', type: 'number' },
      { key: 'estimated_revenue', type: 'number' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['external_id', 'name'],
    defaultSort: { column: 'start_date', direction: 'desc' },
  },
  {
    tableKey: 'pms_reservations',
    permissionKey: 'raw:pms_reservations',
    label: 'PMS Reservations',
    group: 'PMS',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'event_id', type: 'number' },
      // pms_reservations.external_id was dropped in V104 (dead
      // write-only field; the real MEWS identity sits on
      // external_reservation_id). Don't add it back.
      { key: 'external_reservation_id' },
      { key: 'guest_name' },
      { key: 'arrival_date', type: 'date' },
      { key: 'departure_date', type: 'date' },
      // V102 dropped room_type / room_type_code and replaced them with
      // the requested/assigned pair ('comfort' | 'economy' tier).
      { key: 'requested_room_type' },
      { key: 'assigned_room_type' },
      { key: 'assigned_room_type_id', type: 'number' },
      // Block linkage. `pms_block_id` is the legacy on-block link and is NULL
      // on every modern row — every hotel pickup is a standalone MEWS
      // reservation. `debited_block_id` (V120, backfilled V127) is the real
      // one: the block whose capacity counter the create decremented.
      //
      // Since the 2026-08-31 policy change it may point at a block whose
      // room_type_id DIFFERS from assigned_room_type_id above. A block is an
      // event-level headcount cap, so a guest in a category the event holds no
      // block for still debits one — its own category's block when that has
      // room, else the block with the largest remaining capacity.
      //
      // Plain ids rather than joins on purpose: two FKs into pms_blocks would
      // need PostgREST disambiguation hints to embed, and a bad embed 400s the
      // whole table (see the property_id note below).
      { key: 'pms_block_id', type: 'number' },
      { key: 'debited_block_id', type: 'number' },
      { key: 'adult_count', type: 'number' },
      { key: 'child_count', type: 'number' },
      { key: 'status' },
      { key: 'property_id', type: 'number' },
      { key: 'created_at', type: 'datetime' },
    ],
    // No FK on pms_reservations.property_id → properties.id, so the
    // embed `property:properties(name)` 400s. We surface the numeric
    // property_id column above instead. Add the FK in a migration and
    // restore the join when it exists.
    joins: [
      { fk: 'event_id', alias: 'event', table: 'events', displayColumns: ['name'], label: 'Event' },
    ],
    searchableColumns: ['external_reservation_id', 'guest_name'],
    numericFilterColumn: 'event_id',
    defaultSort: { column: 'arrival_date', direction: 'desc' },
  },
  {
    tableKey: 'pms_blocks',
    permissionKey: 'raw:pms_blocks',
    label: 'PMS Blocks',
    group: 'PMS',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'event_id', type: 'number' },
      // pms_blocks.external_id was dropped in V103 (dead field; the
      // real MEWS identity lives on external_block_id).
      { key: 'external_block_id' },
      { key: 'name' },
      { key: 'arrival_date', type: 'date' },
      { key: 'departure_date', type: 'date' },
      // V127 invariant: room_count = max(0, rooms_requested - consumed).
      // `rooms_requested` is INTENT — the only one ever set directly.
      // `room_count` is DERIVED (the hold remaining) and is written nowhere
      // outside services/block/blockCapacity.ts. Seeing both side by side is
      // what tells you whether a block has actually converged after a pickup.
      { key: 'rooms_requested', type: 'number' },
      { key: 'room_count', type: 'number' },
      // V118: free-text room_category/rate replaced by FKs (room_type_id →
      // pms_room_types, rate_id → rates). Names shown via the joins below.
      { key: 'room_type_id', type: 'number' },
      { key: 'rate_id', type: 'number' },
      { key: 'status' },
      { key: 'property_id', type: 'number' },
      { key: 'created_at', type: 'datetime' },
    ],
    // No FK on pms_blocks.property_id → properties.id (same situation
    // as pms_reservations). property_id stays as a numeric column.
    joins: [
      { fk: 'event_id', alias: 'event', table: 'events', displayColumns: ['name'], label: 'Event' },
      { fk: 'room_type_id', alias: 'room_type', table: 'pms_room_types', displayColumns: ['name'], label: 'Room type' },
      { fk: 'rate_id', alias: 'rate', table: 'rates', displayColumns: ['name'], label: 'Rate' },
    ],
    searchableColumns: ['external_block_id', 'name'],
    numericFilterColumn: 'event_id',
    defaultSort: { column: 'arrival_date', direction: 'desc' },
  },
  {
    tableKey: 'spaces',
    permissionKey: 'raw:spaces',
    label: 'Spaces',
    group: 'Setup',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'name' },
      { key: 'code' },
      { key: 'room_type' },
      { key: 'capacity_classroom', type: 'number' },
      { key: 'capacity_theatre', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'floor_id', type: 'number' },
      { key: 'is_active', type: 'boolean' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
      { fk: 'floor_id', alias: 'floor', table: 'floors', displayColumns: ['name'], label: 'Floor' },
    ],
    searchableColumns: ['name', 'code'],
    defaultSort: { column: 'property_id', direction: 'asc' },
  },
  {
    tableKey: 'event_space_bookings',
    permissionKey: 'raw:event_space_bookings',
    label: 'Event Space Bookings',
    group: 'Core',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'event_id', type: 'number' },
      { key: 'space_id', type: 'number' },
      { key: 'session_id', type: 'number' },
      { key: 'start_time', type: 'datetime' },
      { key: 'end_time', type: 'datetime' },
      { key: 'setup_type_id', type: 'number' },
      { key: 'expected_pax', type: 'number' },
      { key: 'custom_name' },
      { key: 'status' },
    ],
    joins: [
      { fk: 'event_id', alias: 'event', table: 'events', displayColumns: ['name'], label: 'Event' },
      { fk: 'space_id', alias: 'space', table: 'spaces', displayColumns: ['name'], label: 'Space' },
      { fk: 'setup_type_id', alias: 'setup_type', table: 'space_setup_types', displayColumns: ['name'], label: 'Setup Type' },
    ],
    searchableColumns: ['custom_name'],
    numericFilterColumn: 'event_id',
    defaultSort: { column: 'start_time', direction: 'desc' },
  },
  {
    tableKey: 'products',
    permissionKey: 'raw:products',
    label: 'Products',
    group: 'Setup',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'name' },
      { key: 'sku' },
      { key: 'category_id', type: 'number' },
      { key: 'product_type' },
      { key: 'charging_type' },
      { key: 'is_stockable', type: 'boolean' },
      { key: 'is_active', type: 'boolean' },
      { key: 'mews_service_id' },
      { key: 'tax_code' },
      { key: 'property_id', type: 'number' },
    ],
    // No FK on products.property_id → properties.id (same situation
    // as pms_reservations / pms_blocks). property_id stays numeric.
    joins: [
      { fk: 'category_id', alias: 'category', table: 'product_categories', displayColumns: ['name'], label: 'Category' },
    ],
    searchableColumns: ['name', 'sku'],
    defaultSort: { column: 'property_id', direction: 'asc' },
  },
  {
    tableKey: 'event_products',
    permissionKey: 'raw:event_products',
    label: 'Event Products',
    group: 'Core',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'event_id', type: 'number' },
      { key: 'product_id', type: 'number' },
      { key: 'product_name' },
      // Since V162 `quantity` is the operator's BASE; what MEWS was posted
      // lives in the snapshot (per target) and, for a charged line, in
      // total_price (BE #578) and the consumption rows.
      { key: 'quantity', label: 'Quantity (base)', type: 'number' },
      { key: 'unit_price', type: 'number' },
      { key: 'total_price', type: 'number' },
      { key: 'currency' },
      { key: 'charging_type' },
      { key: 'charge_to_type' },
      { key: 'charge_to_ids', label: 'Selected participant IDs' },
      { key: 'status' },
      { key: 'is_charged_to_mews', type: 'boolean' },
      { key: 'mews_sync_requested_at', label: 'MEWS sync requested', type: 'datetime' },
      { key: 'mews_order_id', label: 'MEWS order refs' },
      { key: 'charge_resolution_snapshot', label: 'Snapshot', type: 'json' },
      { key: 'source' },
      { key: 'package_id', type: 'number' },
      { key: 'created_at', type: 'datetime' },
      { key: 'updated_at', label: 'Last updated', type: 'datetime' },
    ],
    joins: [
      { fk: 'event_id', alias: 'event', table: 'events', displayColumns: ['name'], label: 'Event' },
      { fk: 'product_id', alias: 'product', table: 'products', displayColumns: ['name'], label: 'Product' },
      { fk: 'package_id', alias: 'package', table: 'product_packages', displayColumns: ['name'], label: 'Package' },
    ],
    // Never the jsonb column: ilike on jsonb 400s the whole grid.
    searchableColumns: ['product_name', 'mews_order_id'],
    numericFilterColumn: 'event_id',
    defaultSort: { column: 'id', direction: 'desc' },
  },
  {
    // The exact service days beneath a charged line — rule lines since V174,
    // manual lines since BE #578. One row per line and date, quantity summed
    // over the line's payers.
    tableKey: 'event_product_consumptions',
    permissionKey: 'raw:event_product_consumptions',
    label: 'Event Product Consumptions',
    group: 'Core',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'event_product_id', type: 'number' },
      { key: 'consumption_date', type: 'date' },
      { key: 'quantity', type: 'number' },
      { key: 'amount', type: 'number' },
      { key: 'allocation_source' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'event_product_id', alias: 'event_product', table: 'event_products', displayColumns: ['product_name'], label: 'Line' },
    ],
    searchableColumns: [],
    numericFilterColumn: 'event_product_id',
    defaultSort: { column: 'consumption_date', direction: 'desc' },
  },
  {
    tableKey: 'event_sessions',
    permissionKey: 'raw:event_sessions',
    label: 'Event Sessions',
    group: 'Core',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'event_id', type: 'number' },
      { key: 'name' },
      { key: 'session_date', type: 'date' },
      { key: 'start_time', type: 'datetime' },
      { key: 'end_time', type: 'datetime' },
      { key: 'setup_type_id', type: 'number' },
      { key: 'pax', type: 'number' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'event_id', alias: 'event', table: 'events', displayColumns: ['name'], label: 'Event' },
      { fk: 'setup_type_id', alias: 'setup_type', table: 'space_setup_types', displayColumns: ['name'], label: 'Setup Type' },
    ],
    searchableColumns: ['name'],
    numericFilterColumn: 'event_id',
    defaultSort: { column: 'start_time', direction: 'desc' },
  },
  {
    tableKey: 'tasks',
    permissionKey: 'raw:tasks',
    label: 'Tasks',
    group: 'Core',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'event_id', type: 'number' },
      { key: 'assigned_to_user_id', type: 'number' },
      { key: 'title' },
      { key: 'priority' },
      { key: 'status' },
      { key: 'due_date', type: 'date' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'event_id', alias: 'event', table: 'events', displayColumns: ['name'], label: 'Event' },
      { fk: 'assigned_to_user_id', alias: 'assignee', table: 'users', displayColumns: ['full_name', 'email'], label: 'Assignee' },
    ],
    searchableColumns: ['title'],
    numericFilterColumn: 'event_id',
    defaultSort: { column: 'due_date', direction: 'asc' },
  },
  {
    tableKey: 'pms_room_types',
    permissionKey: 'raw:pms_room_types',
    label: 'PMS Room Types',
    group: 'PMS',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'mews_category_id' },
      { key: 'name' },
      { key: 'short_name' },
      { key: 'capacity', type: 'number' },
      { key: 'extra_capacity', type: 'number' },
      { key: 'ordering', type: 'number' },
      { key: 'is_active', type: 'boolean' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['name', 'short_name', 'mews_category_id'],
    defaultSort: { column: 'ordering', direction: 'asc' },
  },
  {
    tableKey: 'rates',
    permissionKey: 'raw:rates',
    label: 'Rates',
    group: 'Pricing',
    // `rates` is now pure metadata. Migration archaeology:
    //   V64: ADD mews_rate_id, description; DROP amount, currency
    //   V66: ADD stream
    //   V69: ADD is_default
    //   V73: DROP category_id, unit (+ drops rate_categories table)
    // Real money lives on pms_room_type_prices / product_prices keyed
    // by rate_id; currency is sourced from the linked property.
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'name' },
      { key: 'description' },
      { key: 'stream' },
      { key: 'mews_rate_id' },
      { key: 'mews_price_group_id' },
      { key: 'mews_service_id' },
      { key: 'is_default', type: 'boolean' },
      { key: 'is_dayguest_default', type: 'boolean' },
      { key: 'is_active', type: 'boolean' },
      { key: 'created_at', type: 'datetime' },
      { key: 'updated_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['name', 'description', 'mews_rate_id', 'mews_price_group_id', 'mews_service_id'],
    defaultSort: { column: 'name', direction: 'asc' },
  },
  {
    tableKey: 'tax_rates',
    permissionKey: 'raw:tax_rates',
    label: 'Tax Rates',
    group: 'Pricing',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'name' },
      { key: 'rate', type: 'number' },
      { key: 'is_default', type: 'boolean' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['name'],
    defaultSort: { column: 'rate', direction: 'asc' },
  },
  {
    tableKey: 'rules',
    permissionKey: 'raw:rules',
    label: 'Rules',
    group: 'Pricing',
    // V117 dropped rules.event_stream and rules.rate_id; they moved to
    // the junction tables rule_event_streams and rule_rates so a rule
    // can scope to multiple streams/rates. Don't add them back here —
    // surface them as their own raw entries if needed.
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'name' },
      { key: 'description' },
      { key: 'priority', type: 'number' },
      { key: 'is_active', type: 'boolean' },
      { key: 'valid_from', type: 'date' },
      { key: 'valid_until', type: 'date' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['name', 'description'],
    defaultSort: { column: 'priority', direction: 'desc' },
  },
  {
    tableKey: 'users',
    permissionKey: 'raw:users',
    label: 'Users',
    group: 'Admin',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'email' },
      { key: 'full_name' },
      { key: 'role' },
      { key: 'language' },
      { key: 'is_active', type: 'boolean' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['email', 'full_name'],
    defaultSort: { column: 'created_at', direction: 'desc' },
  },
  {
    tableKey: 'properties',
    permissionKey: 'raw:properties',
    label: 'Properties',
    group: 'Admin',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'name' },
      { key: 'code' },
      { key: 'city' },
      { key: 'country' },
      { key: 'timezone' },
      { key: 'currency' },
      { key: 'is_default', type: 'boolean' },
    ],
    searchableColumns: ['name', 'code'],
    defaultSort: { column: 'id', direction: 'asc' },
  },
  {
    tableKey: 'accounts',
    permissionKey: 'raw:accounts',
    label: 'Accounts',
    group: 'CRM',
    columns: [
      { key: 'id', type: 'number' },
      { key: 'property_id', type: 'number' },
      { key: 'name' },
      { key: 'type' },
      { key: 'email' },
      { key: 'phone' },
      { key: 'city' },
      { key: 'country' },
      { key: 'created_at', type: 'datetime' },
    ],
    joins: [
      { fk: 'property_id', alias: 'property', table: 'properties', displayColumns: ['name'], label: 'Property' },
    ],
    searchableColumns: ['name', 'email'],
    defaultSort: { column: 'name', direction: 'asc' },
  },
  {
    tableKey: 'parties',
    permissionKey: 'raw:parties',
    label: 'Parties (Contacts)',
    group: 'CRM',
    // parties.id is a UUID, not a bigint — leave the type undefined so
    // the renderer treats it as a plain string column.
    columns: [
      { key: 'id' },
      { key: 'type' },
      { key: 'name' },
      { key: 'email' },
      { key: 'bc_id' },
      { key: 'mews_id' },
      { key: 'company_id' },
      { key: 'created_at', type: 'datetime' },
    ],
    searchableColumns: ['name', 'email', 'bc_id', 'mews_id'],
    defaultSort: { column: 'created_at', direction: 'desc' },
  },
]

/** Sorted permission keys exported for the router's any-of guard. */
export const RAW_TABLE_PERMISSION_KEYS = rawTables.map(t => t.permissionKey)
